import { useMemo, useState } from 'react'
import PropTypes from 'prop-types'
import { Typography, Table } from 'antd'
import Chart from 'react-apexcharts'
import { COMPANIES_TRANSLATIONS } from '../../lib/companies'
import ChartTableSwitch from './ChartTableSwitch'

const { Title } = Typography

const COLORS = {
  Medtronic: '#3b82f6',
  Abbott: '#22c55e',
  Boston: '#f59e0b',
  Biotronik: '#ef4444',
  Sorin: '#a855f7',
}

function translateCompany(input) {
  return COMPANIES_TRANSLATIONS[input] || input
}

export default function DevicesSummary({ devicesStats }) {
  const [showChart, setShowChart] = useState(true)
  const piesData = useMemo(
    () =>
      devicesStats.map(deviceTypeData => ({
        title: deviceTypeData.name,
        total: deviceTypeData.count,
        chartData: deviceTypeData.manufacturers.map(data => ({
          value: data.count,
          color: COLORS[translateCompany(data.name)],
          title: translateCompany(data.name),
          key: translateCompany(data.name),
        })),
      })),
    [devicesStats],
  )

  const devicesPerManufacturer = devicesStats.reduce((acc, deviceTypeData) => {
    deviceTypeData.manufacturers.forEach(manufacturerData => {
      const companyName = translateCompany(manufacturerData.name)
      if (!acc[companyName]) {
        acc[companyName] = 0
      }
      acc[companyName] += manufacturerData.count
    })
    return acc
  }, {})

  const chartSeries = useMemo(
    () =>
      Object.keys(devicesPerManufacturer)
        .sort()
        .map(company => ({
          name: company,
          data: piesData.map(
            pieData =>
              pieData.chartData.find(d => d.key === company)?.value || 0,
          ),
        })),
    [devicesPerManufacturer, piesData],
  )

  const chartOptions = useMemo(
    () => ({
      chart: {
        type: 'bar',
        stacked: true,
        toolbar: { show: false },
        zoom: { enabled: false },
        fontFamily: 'inherit',
      },
      plotOptions: {
        bar: {
          horizontal: true,
          barHeight: '50%',
          borderRadius: 0,
        },
      },
      dataLabels: { enabled: false },
      stroke: { show: false },
      xaxis: {
        categories: piesData.map(pieData => pieData.title),
        axisBorder: { show: false },
        axisTicks: { show: false },
        labels: { style: { colors: '#94a3b8', fontSize: '12px' } },
      },
      yaxis: {
        labels: { style: { colors: '#64748b', fontSize: '12px', fontWeight: 500 } },
      },
      grid: {
        borderColor: '#f1f5f9',
        strokeDashArray: 4,
        xaxis: { lines: { show: true } },
        yaxis: { lines: { show: false } },
      },
      legend: {
        position: 'bottom',
        offsetY: 4,
        fontSize: '12px',
        fontWeight: 500,
        labels: { colors: '#64748b' },
        markers: { radius: 3 },
      },
      tooltip: {
        style: { fontSize: '12px' },
        y: {
          formatter: value => Math.round(value).toLocaleString(),
        },
      },
      colors: Object.keys(devicesPerManufacturer).sort().map(
        company => COLORS[company],
      ),
    }),
    [piesData, devicesPerManufacturer],
  )

  return (
    <>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
        {/* level 2 for heading order; the inline style pins the visual size */}
        <Title
          level={2}
          style={{
            margin: 0,
            fontSize: 16,
            fontWeight: 600,
            color: '#0f172a',
            letterSpacing: '-0.005em',
          }}
        >
          Devices summary
        </Title>
        <ChartTableSwitch onChange={value => setShowChart(value === 'chart')} viewMode={showChart ? 'chart' : 'table'} />
      </div>

      {showChart && (
        <Chart
          options={chartOptions}
          series={chartSeries}
          type="bar"
          height={300}
        />
      )}

      {!showChart && (
        <Table
          scroll={{ x: 'max-content' }}
          size="small"
          pagination={false}
          columns={[
            {
              title: 'Manufacturer',
              dataIndex: 'manufacturer',
              key: 'manufacturer',
            },
            {
              title: 'Total',
              dataIndex: 'count',
              key: 'count',
              align: 'center',
              render: value => value.toLocaleString(),
            },
            ...devicesStats.map(deviceTypeData => ({
              title: deviceTypeData.name,
              dataIndex: deviceTypeData.name,
              key: deviceTypeData.name,
              align: 'center',
              render: (_, record) => {
                const manufacturerData = deviceTypeData.manufacturers.find(
                  m => translateCompany(m.name) === record.manufacturer,
                )
                return (manufacturerData ? manufacturerData.count : 0).toLocaleString()
              },
            })),
          ]}
          dataSource={Object.keys(devicesPerManufacturer).map(company => ({
            key: company,
            manufacturer: company,
            count: devicesPerManufacturer[company],
          }))}
        />
      )}
    </>
  )
}

DevicesSummary.propTypes = {
  devicesStats: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
}
